import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Buscador } from "shared/components/headers/headerbusca/buscador/Buscador";
import { Categorias } from "shared/components/categorias/Categorias";
import { LojaHeader } from "shared/components/lojaheader/LojaHeader";
import { ProdutoCard } from "shared/components/modal/ProdutoCard";
import { useProdutosInfinitos } from "shared/hooks/useProdutosInfinitos";
import { ICategoria } from "shared/interfaces/ICategoria";
import { ILojaPublica } from "shared/interfaces/ILojaPublica";
import { buscarLojaPublica } from "shared/services/api/lojas.api";
import {
  buscarCategoriasDaLoja,
  listarProdutosDaLoja,
} from "shared/services/api/storefront.api";

import styles from "./LojaProdutos.module.scss";

const LIMITE = 20;

export const LojaProdutos: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();

  const [loja, setLoja] = useState<ILojaPublica | null>(null);
  const [categorias, setCategorias] = useState<ICategoria[]>([]);
  const [carregandoLoja, setCarregandoLoja] = useState(true);
  const [categoriaSelecionada, setCategoriaSelecionada] =
    useState<ICategoria | null>(null);

  /* ================================
     LOJA E CATEGORIAS
  ================================ */

  useEffect(() => {
    let ativo = true;

    async function carregarLoja() {
      setCarregandoLoja(true);
      setCategoriaSelecionada(null);

      try {
        // Sem categorias a tela ainda funciona; só a loja é obrigatória.
        const [lojaEncontrada, categoriasLoja] = await Promise.all([
          buscarLojaPublica(id),
          buscarCategoriasDaLoja(id).catch(() => []),
        ]);

        if (!ativo) {
          return;
        }

        setLoja(lojaEncontrada);
        setCategorias(categoriasLoja);
      } catch (error) {
        if (ativo) {
          setLoja(null);
          setCategorias([]);
        }
      } finally {
        if (ativo) {
          setCarregandoLoja(false);
        }
      }
    }

    carregarLoja();

    return () => {
      ativo = false;
    };
  }, [id]);

  /* ================================
     PRODUTOS (ROLAGEM INFINITA)
  ================================ */

  const categoriaId = categoriaSelecionada?.id;

  const buscarPagina = useCallback(
    (pagina: number) =>
      listarProdutosDaLoja({
        storeId: id,
        categoryId: categoriaId,
        page: pagina,
        limit: LIMITE,
      }),
    [id, categoriaId],
  );

  const { produtos, temMais, carregando, carregandoInicial, sentinelaRef } =
    useProdutosInfinitos(buscarPagina, `${id}:${categoriaId ?? "todas"}`);

  // Clicar na categoria já selecionada volta para todos os produtos.
  const handleCategoria = (categoria: ICategoria) => {
    setCategoriaSelecionada((atual) =>
      atual?.id === categoria.id ? null : categoria,
    );
  };

  /* ================================
     RENDER
  ================================ */

  if (carregandoLoja) {
    return <p className={styles.mensagem}>Carregando loja...</p>;
  }

  if (!loja) {
    return <p className={styles.mensagem}>Loja não encontrada.</p>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.containerBusca}>
        <Buscador />
      </div>

      <LojaHeader loja={loja} />

      <div className={styles.containerLoja}>
        {categorias.length > 0 && (
          <>
            <div className={styles.containerTitulo}>
              <h3 className={styles.titulo}>Categorias</h3>
            </div>

            <Categorias
              categorias={categorias}
              onCategoriaClick={handleCategoria}
              categoriaSelecionada={categoriaSelecionada}
            />
          </>
        )}

        <section className={styles.produtos}>
          <h3 className={styles.titulo}>
            {categoriaSelecionada?.nome ?? "Todos os produtos"}
          </h3>

          {carregandoInicial ? (
            <div className={styles.mensagem}>Carregando produtos...</div>
          ) : produtos.length === 0 ? (
            <div className={styles.mensagem}>
              Nenhum produto encontrado nesta loja.
            </div>
          ) : (
            <>
              <div className={styles.produtosGrid}>
                {produtos.map((produto) => (
                  <div key={produto.id} className={styles.produtoItem}>
                    <ProdutoCard produto={produto} variant="indicados" />
                  </div>
                ))}
              </div>

              {temMais && (
                <div ref={sentinelaRef} className={styles.sentinela}>
                  {carregando && <span>Carregando mais produtos...</span>}
                </div>
              )}

              {!temMais && (
                <div className={styles.fimLista}>
                  Você viu todos os produtos da loja.
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};
